/**
 * Select mode for the Message Requests list (#3039).
 *
 * The mode is one attribute on the overlay (data-select-mode) and one per row
 * (data-selected). Rows style themselves off those, so a keyset page appended
 * while the mode is active already renders selectable and no pass over the new
 * rows is needed for appearance. Two things still need a per-row pass: the
 * shared swipe module matches on an attribute, and the row is a button only
 * while the mode is on.
 *
 * Drives the list through window.PmRequests (pm/requests.js).
 */
(function () {
  'use strict';

  // Mirrors ThreadController::DECLINE_ALL_MAX_EXCEPTS.
  var EXCEPT_MAX = 1000;

  var active = false;
  var selected = Object.create(null);
  // Select all covers pages not loaded yet, so it is held as a flag plus the
  // rows unticked since, not as a list of ids.
  var allSelected = false;
  var excluded = Object.create(null);
  // The count is taken when the mode opens, which is when the rendered list
  // stops taking refreshes. The cap comes from that same list.
  var modeCount = 0;
  var allCapId = '';
  // One decline-all at a time, across modes: a mode opened again while a call
  // runs waits for it.
  var deletingAll = false;
  // Bumped on every exit, so a call that outlives its mode does not end the next one.
  var modeGen = 0;

  // Captured when the mode opens, because the header shows a selection count in
  // their place and has to read as it did on exit.
  var restingSelectLabel = '';
  var restingDeleteLabel = '';

  function api() {
    return window.PmRequests || null;
  }

  function el(id) {
    return document.getElementById(id);
  }

  /**
   * The selected ids that still have a row, pruning the rest as it reads.
   * An accept or a block on another device takes a row out from under the
   * mode, and a left-behind id is declined and counted as if it were there.
   */
  function selectedIds() {
    var a = api();
    var ids = Object.keys(selected);
    if (!a || typeof a.row !== 'function') return ids;
    var live = [];
    for (var i = 0; i < ids.length; i++) {
      if (a.row(ids[i])) {
        live.push(ids[i]);
      } else {
        delete selected[ids[i]];
      }
    }
    return live;
  }

  /** The unticked ids that still have a row, pruned the same way as selectedIds. */
  function excludedIds() {
    var a = api();
    var ids = Object.keys(excluded);
    if (!a || typeof a.row !== 'function') return ids;
    var live = [];
    for (var i = 0; i < ids.length; i++) {
      if (a.row(ids[i])) {
        live.push(ids[i]);
      } else {
        delete excluded[ids[i]];
      }
    }
    return live;
  }

  function busy() {
    return deletingAll;
  }

  function selectedCount() {
    if (!allSelected) return selectedIds().length;
    // Rows that leave lower the count, and arrivals do not raise it.
    var a = api();
    var live = a ? a.getCount() : modeCount;
    return Math.max(0, Math.min(modeCount, live) - excludedIds().length);
  }

  function isRowSelected(id) {
    if (!id) return false;
    return allSelected ? !excluded[id] : !!selected[id];
  }

  /** The newest rendered request, so one that arrives during the confirm stays. */
  function newestLastMsgId() {
    var a = api();
    var rows = a ? a.rows() : [];
    var max = '';
    for (var i = 0; i < rows.length; i++) {
      var v = rows[i].getAttribute('data-last-msg-id') || '';
      if (/^[1-9][0-9]*$/.test(v) && (max === '' || a.idIsOlder(max, v))) max = v;
    }
    return max;
  }

  /** The row's own control: opens the conversation, or toggles the selection. */
  function rowButton(row) {
    return row.querySelector('.message-request-open');
  }

  /** Put a row back to how it reads outside select mode. */
  function resetRow(row) {
    row.setAttribute('data-swipe-row', '');
    row.removeAttribute('data-selected');
    var btn = rowButton(row);
    if (btn) btn.removeAttribute('aria-pressed');
  }

  /**
   * Match one row to the current mode: swipe off, pressed state, selection.
   *
   * aria-pressed goes on the row's button, never on the row: the row is the
   * listitem its list needs, and a role on it would make the button
   * presentational.
   */
  function applyModeToRow(row) {
    if (!active) {
      resetRow(row);
      return;
    }
    row.removeAttribute('data-swipe-row');
    var isSelected = isRowSelected(row.getAttribute('data-thread-id'));
    var btn = rowButton(row);
    if (btn) btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    if (isSelected) {
      row.setAttribute('data-selected', 'true');
    } else {
      row.removeAttribute('data-selected');
    }
  }

  function applyModeToRows() {
    var a = api();
    if (!a) return;
    var rows = a.rows();
    for (var i = 0; i < rows.length; i++) applyModeToRow(rows[i]);
  }

  function updateChrome() {
    var a = api();
    if (!a) return;

    var count = selectedCount();
    var selectBtn = el('message-requests-view-select');
    var selectAllBtn = el('message-requests-view-select-all');
    if (selectAllBtn) {
      selectAllBtn.textContent = allSelected
        ? window.tOr('common.pm.requests_deselect_all', 'Deselect all')
        : window.tOr('common.pm.requests_select_all', 'Select all');
      selectAllBtn.disabled = busy();
    }
    if (selectBtn) selectBtn.disabled = busy();
    var bar = el('message-requests-view-selectbar');
    if (bar) bar.setAttribute('aria-busy', busy() ? 'true' : 'false');
    var idleIcon = el('message-requests-view-delete-icon');
    var busyIcon = el('message-requests-view-delete-spinner');
    if (idleIcon) idleIcon.classList.toggle('hidden', busy());
    if (busyIcon) busyIcon.classList.toggle('hidden', !busy());
    var deleteBtn = el('message-requests-view-delete-selected');
    var deleteLabel = el('message-requests-view-delete-selected-label');

    if (!active) {
      // The labels are empty until enter() captures them, so the server text stays.
      if (selectBtn && restingSelectLabel) selectBtn.textContent = restingSelectLabel;
      if (deleteBtn) deleteBtn.disabled = true;
      if (deleteLabel && restingDeleteLabel) deleteLabel.textContent = restingDeleteLabel;
      // Re-applies the count line and the Select button's own visibility.
      a.setCount(a.getCount());
      return;
    }

    if (selectBtn) {
      selectBtn.textContent = window.tOr('common.done', 'Done');
      selectBtn.classList.remove('hidden');
    }
    // The count line is the one live region in the header, so the selection
    // count is announced there. The heading stays put: a heading that renames
    // itself moves under the user in the heading list.
    var countLine = el('message-requests-view-count');
    if (countLine) {
      // Unhidden first: a live region announces nothing while it is display
      // none, and revealing it afterwards does not replay the text.
      countLine.classList.remove('hidden');
      countLine.textContent = window.tOr('common.pm.requests_selected_count', count + ' selected', { count: count });
    }

    if (deleteBtn) deleteBtn.disabled = count === 0 || busy();
    var deleting = window.tOr('common.pm.requests_deleting', 'Deleting…');
    if (deleteLabel) {
      if (busy()) {
        deleteLabel.textContent = deleting;
      } else {
        deleteLabel.textContent = count === 0
          ? restingDeleteLabel
          : window.tOr('common.pm.requests_delete_selected', 'Delete ' + count, { count: count });
      }
    }
    // The count line is the live region, so the long call is announced there.
    if (countLine && busy()) countLine.textContent = deleting;
  }

  // Select mode holds the rendered list still: a refresh that replaced it would
  // discard the selection. pm/requests-live.js waits for the mode to end.
  function announceMode() {
    try {
      document.dispatchEvent(new CustomEvent('pm:requests-select-changed', {
        detail: { active: active }
      }));
    } catch (e) { /* CustomEvent unavailable: nothing downstream is required */ }
  }

  function enter() {
    var a = api();
    var inner = a && a.inner();
    if (active || !inner) return;

    // A delete still inside its undo window is committed rather than carried
    // into a mode where its row cannot be shown again.
    if (window.PmRequestsUndo) window.PmRequestsUndo.commitNow();
    if (window.SwipeActions) window.SwipeActions.closeAll();

    var selectBtn = el('message-requests-view-select');
    var deleteLabel = el('message-requests-view-delete-selected-label');
    restingSelectLabel = selectBtn ? selectBtn.textContent.trim() : '';
    restingDeleteLabel = deleteLabel ? deleteLabel.textContent.trim() : '';

    active = true;
    selected = Object.create(null);
    allSelected = false;
    excluded = Object.create(null);
    modeCount = a.getCount();
    allCapId = '';
    inner.setAttribute('data-select-mode', 'on');
    applyModeToRows();
    updateChrome();
    announceMode();
  }

  function exit() {
    if (!active) return;
    modeGen++;
    active = false;
    selected = Object.create(null);
    allSelected = false;
    excluded = Object.create(null);
    allCapId = '';

    var a = api();
    var inner = a && a.inner();
    if (inner) inner.removeAttribute('data-select-mode');
    applyModeToRows();
    updateChrome();
    announceMode();
  }

  window.PmRequestsSelect = {
    isActive: function () {
      return active;
    },

    isBusy: busy,

    syncChrome: function () {
      updateChrome();
    },

    enter: enter,
    exit: exit,

    toggle: function () {
      if (busy()) return;
      if (active) {
        exit();
      } else {
        enter();
      }
    },

    toggleRow: function (row) {
      if (!active || !row) return;
      var id = row.getAttribute('data-thread-id');
      if (!id || busy()) return;
      var map = allSelected ? excluded : selected;
      if (map[id]) {
        delete map[id];
      } else {
        map[id] = true;
      }
      applyModeToRow(row);
      updateChrome();
    },

    selectedIds: selectedIds,

    toggleAll: function () {
      if (!active || busy()) return;
      var capId = allSelected ? '' : newestLastMsgId();
      // Nothing rendered gives no snapshot, and no cap would sweep up later requests.
      if (!allSelected && !capId) return;
      allSelected = !allSelected;
      allCapId = capId;
      selected = Object.create(null);
      excluded = Object.create(null);
      applyModeToRows();
      updateChrome();
    },

    /**
     * Delete a manual selection through the shared undo window. The window
     * opens before the mode closes, so the hold that keeps a server refresh
     * off the rendered rows never lifts between the two. Select all asks
     * first and declines on the server instead.
     */
    deleteSelected: function (fromKeyboard) {
      var a = api();
      if (!active || !a || busy()) return;
      if (allSelected) {
        confirmDeleteAll();
        return;
      }
      var ids = selectedIds();
      if (!ids.length) return;

      // Reset the rows before they leave. Undo puts them back into a list that
      // is out of select mode, and a row still carrying the mode's attributes
      // would come back checked and unswipeable.
      selected = Object.create(null);
      for (var i = 0; i < ids.length; i++) {
        var row = a.row(ids[i]);
        if (row) resetRow(row);
      }

      // The rows above are already reset and select mode is about to close, so
      // a schedule that opens no window has to say so. Silence here reads as a
      // delete that worked.
      var started = window.PmRequestsUndo
        ? window.PmRequestsUndo.scheduleBulk(ids, fromKeyboard)
        : false;
      if (!started && typeof a.actionError === 'function') a.actionError();
      exit();
    }
  };

  /**
   * Select all reaches rows this page never loaded, so it cannot run through
   * the undo window, which restores rendered rows. It asks first instead.
   */
  function confirmDeleteAll() {
    var count = selectedCount();
    if (!count) return;
    // The count lives in the title: focus opens on Cancel, so the confirm label is not read first.
    var title = api().pluralText('common.pm.requests_delete_all_title', count, 'Delete ' + count + ' message requests?');
    var body = window.tOr('common.pm.requests_delete_all_body', "You can't undo this.");
    if (!window.ConfirmModal) {
      if (window.confirm(title + '\n\n' + body)) deleteAll();
      return;
    }
    window.ConfirmModal.show({
      title: title,
      message: body,
      confirmText: window.tOr('common.pm.requests_delete_selected', 'Delete ' + count, { count: count }),
      type: 'danger',
      focus: 'cancel',
      onConfirm: deleteAll,
      onCancel: function () {
        var btn = el('message-requests-view-delete-selected');
        if (btn) btn.focus();
      }
    });
  }

  // One comma-joined field, cap first: PHP drops form variables past
  // max_input_vars, and a lost cap would reach requests newer than the snapshot.
  function declineAllBody(except, capId) {
    var body = new URLSearchParams();
    body.append('max_last_msg_id', capId);
    if (except.length) body.append('except_thread_ids', except.join(','));
    return body;
  }

  /**
   * One call stops at the server's per-call bound and says so with has_more.
   * The same snapshot and exceptions make each repeat pick up where it stopped.
   */
  function declineAllPages(a, except, capId, result) {
    return a.apiPost('/thread/decline-all', { body: declineAllBody(except, capId) })
      .then(function (resp) { return resp.json(); })
      .then(function (json) {
        var data = (json && json.data) || {};
        var ids = data.thread_ids || [];
        for (var i = 0; i < ids.length; i++) result.ids.push(String(ids[i]));
        result.failed += Number(data.failed) || 0;
        if (data.has_more && ids.length) return declineAllPages(a, except, capId, result);
        return result;
      });
  }


  function deleteAll() {
    var a = api();
    if (!active || !allSelected || !a || busy() || !allCapId) return;
    var except = excludedIds();
    if (except.length > EXCEPT_MAX) {
      a.actionError();
      return;
    }
    // A row whose undo window is still open has no row to except, so the
    // server would decline it under the window and its Undo would fail.
    if (window.PmRequestsUndo) window.PmRequestsUndo.commitNow();

    // The mode stays on while the call runs: it holds the rendered list
    // against a refresh, and the reload at the end replaces the list once.
    deletingAll = true;
    var gen = modeGen;
    // The live count line carries the progress, and Back under focus would
    // close the overlay on a repeated Enter.
    updateChrome();
    var countLine = el('message-requests-view-count');
    if (countLine) countLine.focus();

    var countAtStart = a.getCount();
    var result = { ids: [], failed: 0 };
    var declinedIds = result.ids;
    var finish = function (failed) {
      deletingAll = false;
      // Rows the server reports as failed are still pending, so the user hears it.
      failed = failed || result.failed > 0;
      // A failed call can still have declined rows, so it marks the queue changed too.
      if (declinedIds.length || failed) a.markChanged();
      if (declinedIds.length) a.closeOpenRequests(declinedIds);
      // The toast outlives the overlay reload, which replaces the count line.
      if (declinedIds.length && typeof window.showSuccess === 'function') {
        window.showSuccess(a.deletedCountText(declinedIds.length));
      }
      if (failed) a.actionError();
      // Refetches the server count after select mode releases the list.
      var settle = function () {
        try {
          document.dispatchEvent(new CustomEvent('pm:requests-decline-all-settled'));
        } catch (e) { /* CustomEvent unavailable: the next refresh corrects the count */ }
      };
      // A reopened overlay still loading refetches on markChanged.
      if (!a.inner()) { settle(); return; }
      // The reload can fail, so declined rows leave the rendered list first.
      if (typeof a.detachRow === 'function') {
        for (var i = 0; i < declinedIds.length; i++) a.detachRow(declinedIds[i]);
      }
      // Left during the call: a later mode keeps its own selection. A held
      // list turns the reload into a stale mark, which runs when that mode ends.
      if (gen !== modeGen) {
        updateChrome();
        settle();
        a.reloadOverlay();
        return;
      }
      // A failed call can still have declined rows before its response was
      // lost, so the list reloads from the server on failure too.
      // The reload and the banner refetch can fail, so the count line takes a
      // local count. A lower count from a live refresh wins.
      a.setCount(Math.max(0, Math.min(a.getCount(), countAtStart - declinedIds.length)));
      exit();
      if (typeof a.refreshEmptyState === 'function') a.refreshEmptyState();
      settle();
      a.reloadOverlay();
    };
    declineAllPages(a, except, allCapId, result).then(
      function () { finish(false); },
      function () { finish(true); }
    );
  }

  function watchAppendedRows() {
    var a = api();
    if (!a || typeof a.onRowsAppended !== 'function') return;
    a.onRowsAppended(function (rows) {
      if (!active) return;
      for (var i = 0; i < rows.length; i++) applyModeToRow(rows[i]);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchAppendedRows);
  } else {
    watchAppendedRows();
  }
})();
